import React, { useCallback, useEffect, useRef, useState } from "react"
import Head from "next/head"
import useScreenOrientation from "@/hooks/useScreenOrientation"
import useDeferredVideo from "@/hooks/useDeferredVideo"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import {
  convertStringToBoolean,
  createIdFromString,
  getRandom,
  sanitizeInnerHtml,
} from "@/utils/helper"
import Action from "@/components/Default/Teaser/v1/Action"
import Description from "@/components/Default/Teaser/v1/Description"
import { useSelector } from "react-redux"
import { selectGenericState } from "@/store/features/genericSlice"
import { useInView } from "react-intersection-observer"
import { VIDEO_EVENTS } from "@/constants/index"
import { getConfig } from "@/constants/config"
import { useTranslation } from "next-i18next"
import {
  onClickHeroVideo,
  onFavVideo,
  onPlayVideoInModal,
  buildPosterSrcSet,
  onShareVideo,
  onPlayVideo,
  onVideoInteraction,
  onVolumeChange,
  onPlayInspireVideo,
  onPlayInspireVideoClose,
} from "@/utils/videoHelper"
import SocialShare from "@/components/cart/OrderSummary/SocialShare"
import { PURE_TEXT_REGEX } from "@/constants/regex"

const Video = props => {
  const {
    data: {
      videoTitle = "",
      videoMobile = "",
      videoMobileLandscape = "",
      videoMobilePoster = "",
      videoDesktopPoster = "",
      videoDesktop = "",
      muteEnableAutoPlay = "false",
      hideEnableAutoPlay = "false",
      alt = "",
      appliedCssClassNames = "",
      items = [],
      enableLoop = "false",
      blackBgCta = "false",
      offWhiteBg = "false",
      textLinkCta = "false",
      textLinkCtaOffWhite = "false",
      enablePopUPVideo = "false",
      modalCta = "",
      modalVideoDesktop = "",
      modalVideoMobile = "",
      modalVideoMobileLandscape = "",
      actionsEnabled: videoActionsEnabled = "false",
      teaserProperties: {
        title = "",
        description = "",
        linkURLRef = false,
        linkCTA = "",
        linkNewTab = false,
        videoPosterMobileLandscape = "",
        controlType = "",
        pageType = "",
        componentPosition = "",
        componentZone = "",
        componentLinkType = "",
        playsAboveFold = "false",
        eventName = "",
        dynamicTabName = "",
        eventAction = "",
        caption = "",
        pretitle = "",
        actionsEnabled = false,
        actions = [],
        linkAll = false,
        dataLayerLinkType = "",
        mainTitle = "",
        enableFav = false,
        enableShare = false,
        linkDescription = false,
        propertiesPageType = "",
      } = {},
    } = {},
  } = props

  const { eventPageType } = useSelector(selectGenericState)
  const { t } = useTranslation("common")

  const { winWidth } = useWindowDimensions()
  const screenOrientation = useScreenOrientation()
  const [video, setVideo] = useState("")
  const [dynamicId, setDynamicId] = useState("")
  const [videoPlayed, setVideoPlayed] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [showSocialShare, setShowSocialShare] = useState(false)
  const [isFavourited, setIsFavourited] = useState(false)
  const [modelVideo, setModalVideo] = useState("")
  const [favVideoId, setFavVideoId] = useState("")
  const [flagState, setFlagState] = useState({
    flag25: false,
    flag50: false,
    flag75: false,
    flag100: false,
  })
  // Track if video was started by user
  const isUserInitiatedRef = useRef(false)
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.3,
  })

  const videoContainerRef = useRef(null)
  const videoRef = useRef(null)
  const inspireRef = useRef(null)
  const closeBtnRef = useRef(null)
  const [general, setGeneral] = useState(null)

  const getVideoId = video => {
    return decodeURI(video)?.split("PAWEB/")[1]?.toLowerCase()
  }

  const getVideoAccessibleName = () => {
    const candidate =
      videoTitle || title || modalCta || componentLinkType || caption || ""
    const normalized = String(candidate).replace(PURE_TEXT_REGEX, "").trim()
    return normalized || t("kf.video.label.accessibleName")
  }

  const videoAccessibleName = getVideoAccessibleName()

  useEffect(() => {
    getConfig().then(config => {
      setGeneral(config?.general)
    })
  }, [])

  useEffect(() => {
    if (videoTitle) {
      const dynamicId = createIdFromString(videoTitle)
      setDynamicId(dynamicId)
    }
  }, [videoTitle])

  const setVideoSource = useCallback(() => {
    const isDesktop = window.matchMedia("(min-width: 1025px)").matches
    const isLandscape = window.matchMedia("(orientation: landscape)").matches

    const selectedVideo = isDesktop
      ? videoDesktop
      : isLandscape
      ? videoMobileLandscape
      : videoMobile

    if (video && video !== selectedVideo) setVideoReady(false)
    if (isDesktop) {
      setVideo(videoDesktop)
      setModalVideo(modalVideoDesktop)
    } else if (isLandscape) {
      setVideo(videoMobileLandscape)
      setModalVideo(modalVideoMobileLandscape)
    } else {
      setVideo(videoMobile)
      setModalVideo(modalVideoMobile)
    }
  }, [
    video,
    videoMobile,
    videoMobileLandscape,
    videoDesktop,
    modalVideoDesktop,
    modalVideoMobile,
    modalVideoMobileLandscape,
    screenOrientation,
    winWidth,
  ])

  const isAboveFold = convertStringToBoolean(playsAboveFold)
  const unloadVideo = useCallback(() => {
    videoRef?.current?.pause()
    inspireRef?.current?.pause()
    setVideo("")
    setVideoPlayed(false)
    setVideoReady(false)
    setIsVideoPlaying(false)
    setModalVideo("")
  }, [])
  const { autoPlayAllowed, clearPlayRequest, playRequested, requestVideoLoad } =
    useDeferredVideo({
      inView,
      isAboveFold,
      loadVideo: setVideoSource,
      unloadVideo,
    })
  const [modalPlayRequested, setModalPlayRequested] = useState(false)
  const [inspirePlayRequested, setInspirePlayRequested] = useState(false)

  useEffect(() => {
    if (!inView) {
      setModalPlayRequested(false)
      setInspirePlayRequested(false)
    }
  }, [inView])

  useEffect(() => {
    if (video && playRequested && videoRef.current) {
      const playPromise = videoRef.current.play()
      playPromise?.catch(() => {})
      clearPlayRequest()
    }
  }, [clearPlayRequest, playRequested, video])

  useEffect(() => {
    if (video && modalPlayRequested && videoRef.current) {
      setModalPlayRequested(false)
      onPlayVideoInModal(
        { preventDefault: () => {} },
        propertiesPageType,
        videoContainerRef?.current,
        videoRef?.current
      )
    }
  }, [modalPlayRequested, propertiesPageType, video])

  useEffect(() => {
    if (isVideoPlaying) setVideoPlayed(true)
  }, [isVideoPlaying])

  const eventActionTabName = dynamicTabName || "zoneForVideo"
  const eventActionName = `${dynamicTabName || "rm"}:video:rm`

  const onTimeUpdate = event => {
    const videoDuration = inspireRef.current.duration
    const percent100 = Math.floor(videoDuration)
    const currentTime = Math.floor(inspireRef.current.currentTime)
    if (
      (muteEnableAutoPlay === "true" ? percent100 - 1 : percent100) ===
        currentTime &&
      flagState.flag100 === false
    ) {
      setFlagState(prevState => ({
        ...prevState,
        flag100: true,
      }))
      addVideoDataLayer(100)
    }
  }

  const getCurrentVideoProgessPercent = () => {
    const videoDuration = inspireRef.current.duration
    const progressTime = Math.floor(inspireRef.current.currentTime)
    return (progressTime / videoDuration) * 100
  }

  const addVideoDataLayer = percentViewed => {
    let eventActionName
    let viewedPercentage
    let internalLinkName
    const {
      VIDEO_PLAY,
      VIDEO_PAUSE,
      VIDEO_VIEWED,
      VIDEO_COMPLETE,
      VIDEO_CLICK,
      PLAY_VIDEO,
      PAUSE_VIDEO,
      CLOSE_VIDEO,
      CLOSE,
    } = VIDEO_EVENTS
    if (percentViewed === VIDEO_CLICK) {
      eventActionName = `${eventPageType?.toLowerCase()}:${videoTitle}:${modalCta?.toLowerCase()}`
      viewedPercentage = getCurrentVideoProgessPercent()
      internalLinkName = modalCta?.toLowerCase()
    } else if (percentViewed === PLAY_VIDEO) {
      eventActionName = `${eventPageType?.toLowerCase()}:${videoTitle}:${VIDEO_PLAY}`
      viewedPercentage = getCurrentVideoProgessPercent()
      internalLinkName = VIDEO_PLAY
    } else if (percentViewed === PAUSE_VIDEO) {
      eventActionName = `${eventPageType?.toLowerCase()}:${videoTitle}:${VIDEO_PAUSE}`
      viewedPercentage = getCurrentVideoProgessPercent()
      internalLinkName = `${viewedPercentage}% ${VIDEO_VIEWED}`
    } else if (percentViewed === CLOSE_VIDEO) {
      eventActionName = `${eventPageType?.toLowerCase()}:${videoTitle}:${CLOSE}`
      viewedPercentage = getCurrentVideoProgessPercent()
      internalLinkName = CLOSE
    } else {
      eventActionName =
        percentViewed === 100
          ? `${eventPageType?.toLowerCase()}:${videoTitle?.toLowerCase()}:${VIDEO_COMPLETE}`
          : `${eventPageType?.toLowerCase()}:${videoTitle?.toLowerCase()}:${percentViewed}% ${VIDEO_VIEWED}`

      viewedPercentage = percentViewed
      internalLinkName =
        viewedPercentage === 100
          ? `${VIDEO_COMPLETE}`
          : `${viewedPercentage}% ${VIDEO_VIEWED}`
    }
    const eventInfo = {
      clickInternalLinks: "true",
      eventAction: eventActionName,
      eventMsg: "n/a",
      eventName: eventActionName,
      eventStatus: "n/a",
      eventType: "navigation",
      internalLinkName: internalLinkName,
      internalLinkPosition: `${eventPageType?.toLowerCase()}`,
      internalLinkType: `${eventPageType?.toLowerCase()}:${
        componentPosition || "navigation" || "teaser"
      }`,
      internalLinkURL: "n/a",
      internalLinkZoneName: `${eventPageType?.toLowerCase()}:${videoTitle?.toLowerCase()}`,
      videoId: `${getVideoId(modelVideo)}`,
      videoName: `${
        videoTitle || componentLinkType.toLowerCase() || "hero video"
      }`,
      videoType: isUserInitiatedRef.current ? "user initiated" : "auto play",
    }
    const { adobeDataLayer: { getState } = {} } = window
    const page = (getState && getState("page")) || {}
    const productInfo = page.productInfo || {}
    window.adobeDataLayer.push({
      event: "cmp:click",
      page,
      eventInfo,
      productInfo,
    })
  }

  const posterLoading = isAboveFold ? "eager" : "lazy"
  const posterFetchPriority = isAboveFold ? "high" : "low"
  const preservePosterLcp = isAboveFold && muteEnableAutoPlay === "true"
  const desktopPosterSrcSet = preservePosterLcp
    ? buildPosterSrcSet(videoDesktopPoster)
    : ""
  const landscapePosterSrcSet = preservePosterLcp
    ? buildPosterSrcSet(videoPosterMobileLandscape)
    : ""
  const mobilePosterSrcSet = preservePosterLcp
    ? buildPosterSrcSet(videoMobilePoster)
    : ""

  useEffect(() => {
    if (modelVideo && inspirePlayRequested && inspireRef.current) {
      setInspirePlayRequested(false)
      onPlayInspireVideo(
        videoRef?.current,
        inspireRef?.current,
        closeBtnRef?.current,
        addVideoDataLayer
      )
    }
  }, [addVideoDataLayer, inspirePlayRequested, modelVideo])

  const handlePlayVideoInModal = event => {
    if (!video && propertiesPageType === "modalWindow") {
      event.preventDefault()
      setModalPlayRequested(true)
      requestVideoLoad()
      return
    }

    onPlayVideoInModal(
      event,
      propertiesPageType,
      videoContainerRef?.current,
      videoRef?.current
    )
  }

  const handlePlayVideo = () => {
    isUserInitiatedRef.current = true
    if (!video) {
      requestVideoLoad({ play: true })
      return
    }

    onPlayVideo(
      videoRef?.current,
      res => setIsVideoPlaying(res),
      videoDesktopPoster,
      videoMobilePoster,
      video,
      isVideoPlaying
    )
  }

  const handlePlayInspireVideo = () => {
    isUserInitiatedRef.current = true
    if (!modelVideo) {
      setInspirePlayRequested(true)
      requestVideoLoad()
      return
    }

    onPlayInspireVideo(
      videoRef?.current,
      inspireRef?.current,
      closeBtnRef?.current,
      addVideoDataLayer
    )
  }

  return (
    <section
      className={`inspire-video video teaser ${appliedCssClassNames}`}
      ref={inViewRef}
    >
      {isAboveFold ? (
        <Head>
          {videoDesktopPoster ? (
            <link
              rel="preload"
              as="image"
              href={videoDesktopPoster}
              media="(min-width: 1025px)"
              {...(desktopPosterSrcSet
                ? { imageSrcSet: desktopPosterSrcSet, imageSizes: "100vw" }
                : {})}
              {...{ fetchpriority: "high" }}
            />
          ) : null}
          {videoPosterMobileLandscape ? (
            <link
              rel="preload"
              as="image"
              href={videoPosterMobileLandscape}
              media="(min-width: 640px) and (max-width: 1024px)"
              {...(landscapePosterSrcSet
                ? { imageSrcSet: landscapePosterSrcSet, imageSizes: "100vw" }
                : {})}
              {...{ fetchpriority: "high" }}
            />
          ) : null}
          {videoMobilePoster ? (
            <link
              rel="preload"
              as="image"
              href={videoMobilePoster}
              media="(max-width: 639px)"
              {...(mobilePosterSrcSet
                ? { imageSrcSet: mobilePosterSrcSet, imageSizes: "100vw" }
                : {})}
              {...{ fetchpriority: "high" }}
            />
          ) : null}
        </Head>
      ) : null}
      <div
        className={`cmp-teaser${
          controlType === "customControl" ? " gbh-custom-control" : ""
        }${propertiesPageType === "modalWindow" ? " gbh-model-window" : ""}${
          hideEnableAutoPlay === "true" ? " gbh-hide-mute" : ""
        }`}
        ref={videoContainerRef}
      >
        <div
          className={`cmp-teaser__image video_poster_height${
            videoPlayed ? " video-played" : ""
          }${videoReady ? " video-ready" : ""}`}
          data-gbh-data-layer={`{"clickInternalLinks":"true","internalLinkPosition":"${eventPageType?.toLowerCase()}:${
            componentPosition || "teaser"
          }","internalLinkZoneName":"${eventPageType?.toLowerCase()}:${
            componentZone || "teaser"
          }","internalLinkURL":"n/a","internalLinkName":"video play","eventName":"${eventPageType?.toLowerCase()}:${eventActionTabName}","internalLinkType":"${eventPageType?.toLowerCase()}:${
            componentLinkType || "teaser"
          }","eventType":"navigation","eventAction":"${eventPageType?.toLowerCase()}:${eventActionTabName}","videoName":"${
            videoTitle || componentLinkType.toLowerCase() || "hero video"
          }","videoId": "${getVideoId(modelVideo)}","videoType":"${
            muteEnableAutoPlay === "true" ? "auto play" : "user initiated"
          }"}`}
          style={showSocialShare ? { overflow: "visible" } : {}}
        >
          <span
            className={`gbh-play-video desktop${
              isVideoPlaying ? " active" : ""
            }`}
            onClick={event => {
              isUserInitiatedRef.current = true
              handlePlayVideoInModal(event)
            }}
          >
            {!(hideEnableAutoPlay === "true") ? (
              <button
                className="playinside desktop"
                id={`videoinnerbtn-${dynamicId}`}
                aria-label="play video"
                onClick={handlePlayVideo}
              />
            ) : null}
          </span>

          {preservePosterLcp || !videoPlayed ? (
            <picture
              className={`poster-img${
                preservePosterLcp ? " gbh-poster-lcp" : ""
              }`}
            >
              <source
                media="(min-width:1025px)"
                srcSet={desktopPosterSrcSet || videoDesktopPoster}
                sizes="100vw"
              />
              <source
                media="(min-width:640px) and (max-width:1024px)"
                srcSet={landscapePosterSrcSet || videoPosterMobileLandscape}
                sizes="100vw"
              />
              <source
                media="(max-width:639px)"
                srcSet={mobilePosterSrcSet || videoMobilePoster}
                sizes="100vw"
              />
              {videoDesktopPoster ? (
                <img
                  src={videoDesktopPoster}
                  alt=""
                  className="desktop-poster"
                  loading={posterLoading}
                  {...{ fetchpriority: posterFetchPriority }}
                />
              ) : null}
            </picture>
          ) : null}

          {video ? (
            <video
              ref={videoRef}
              id={`video-desktop-${dynamicId}`}
              aria-label={videoAccessibleName}
              title={videoAccessibleName}
              width="100%"
              muted={muteEnableAutoPlay === "true"}
              autoPlay={autoPlayAllowed && muteEnableAutoPlay === "true"}
              loop={muteEnableAutoPlay === "true"}
              playsInline
              preload={isAboveFold ? "metadata" : "none"}
              className={`hero-video inpage-video videoelement${
                muteEnableAutoPlay === "true" ? " muted" : ""
              }${playsAboveFold === "true" ? "" : " lazy-video"}${
                preservePosterLcp ? " gbh-preserve-poster-lcp" : ""
              }`}
              onPlaying={() => setVideoReady(true)}
              onVolumeChange={() => onVolumeChange(videoRef?.current)}
              onClick={event => onClickHeroVideo(event, videoRef?.current)}
              {...(controlType === "defaultControl" &&
              hideEnableAutoPlay === "false"
                ? { controls: true }
                : {})}
              onPlay={() =>
                onVideoInteraction(
                  VIDEO_EVENTS.PLAY,
                  () => setVideoPlayed(true),
                  res => setIsVideoPlaying(res),
                  videoRef?.current,
                  video,
                  id => setFavVideoId(id),
                  flagState
                )
              }
              onPause={() =>
                onVideoInteraction(
                  VIDEO_EVENTS.PAUSE,
                  () => setVideoPlayed(true),
                  res => setIsVideoPlaying(res),
                  videoRef?.current,
                  video,
                  flagState
                )
              }
              onEnded={() =>
                onVideoInteraction(
                  VIDEO_EVENTS.ENDED,
                  () => setVideoPlayed(true),
                  res => setIsVideoPlaying(res),
                  videoRef?.current,
                  video,
                  flagState
                )
              }
              onTimeUpdate={onTimeUpdate}
            >
              <source src={video} type="video/mp4" />
              {items.map((transcript, index) => (
                <track
                  key={`video-track-${dynamicId}-${index}`}
                  label={transcript?.languageLabel}
                  kind="captions"
                  srcLang={transcript?.languageCode}
                  src={transcript?.transcriptSrcPath}
                  default={items?.length ? true : false}
                />
              ))}
            </video>
          ) : (
            <video
              aria-label={videoAccessibleName}
              title={videoAccessibleName}
              className={`hero-video inpage-video videoelement${
                muteEnableAutoPlay === "true" ? " muted" : ""
              }${playsAboveFold === "true" ? "" : " lazy-video"}`}
            />
          )}
          {enableLoop === "true" && modelVideo ? (
            <div>
              <video
                ref={inspireRef}
                aria-label={videoAccessibleName}
                title={videoAccessibleName}
                className={`hero-video videoelement inspirevideo ${
                  muteEnableAutoPlay === "true" ? " muted" : ""
                } ${playsAboveFold === "true" ? "" : "lazy-video"}`}
                data-desktop-vid={`${modalVideoDesktop}`}
                data-mobile-vid={modalVideoMobile}
                data-mobile-lscape-vid={modalVideoMobileLandscape}
                onVolumeChange={() => onVolumeChange(inspireRef?.current)}
                id={"herovideo-desktop-1"}
                width="100%"
                playsInline
                preload="none"
                {...(controlType === "defaultControl" &&
                !(hideEnableAutoPlay === "false")
                  ? { controls: true }
                  : {})}
                muted={muteEnableAutoPlay === "true"}
                autoPlay={autoPlayAllowed && muteEnableAutoPlay === "true"}
                loop={muteEnableAutoPlay === "true"}
                onTimeUpdate={onTimeUpdate}
              >
                <source src={modelVideo} type="video/mp4" />
                {items.map((transcript, index) => (
                  <track
                    key={`video-track-${dynamicId}-${index}`}
                    label={transcript?.languageLabel}
                    kind="captions"
                    srcLang={transcript?.languageCode}
                    src={transcript?.transcriptSrcPath}
                    default={items?.length ? true : false}
                  />
                ))}
              </video>
              <div
                className="vidio_popup_close_button inspired-video-popup-close"
                ref={closeBtnRef}
              >
                <button
                  onClick={() => {
                    onPlayInspireVideoClose(
                      videoRef?.current,
                      inspireRef?.current,
                      closeBtnRef?.current,
                      addVideoDataLayer
                    )
                  }}
                >
                  <span aria-hidden="true"></span>
                </button>
              </div>
            </div>
          ) : null}
          {enableLoop === "false" && modelVideo ? (
            <div>
              <video
                ref={inspireRef}
                aria-label={videoAccessibleName}
                title={videoAccessibleName}
                className={`hero-video videoelement inspirevideo ${
                  muteEnableAutoPlay === "true" ? " muted" : ""
                } ${playsAboveFold === "true" ? "" : "lazy-video"}`}
                data-desktop-vid={`${modalVideoDesktop}`}
                data-mobile-vid={modalVideoMobile}
                data-mobile-lscape-vid={modalVideoMobileLandscape}
                onVolumeChange={() => onVolumeChange(inspireRef?.current)}
                id={"herovideo-desktop-1"}
                width="100%"
                playsInline
                preload="none"
                {...(controlType === "defaultControl" &&
                !(hideEnableAutoPlay === "false")
                  ? { controls: true }
                  : {})}
                muted={muteEnableAutoPlay === "true" ? true : false}
                autoPlay={false}
                loop={muteEnableAutoPlay === "true"}
                onTimeUpdate={onTimeUpdate}
              >
                <source src={modelVideo} type="video/mp4" />
                {items.map((transcript, index) => (
                  <track
                    key={`video-track-${dynamicId}-${index}`}
                    label={transcript?.languageLabel}
                    kind="captions"
                    srcLang={transcript?.languageCode}
                    src={transcript?.transcriptSrcPath}
                    default={items?.length ? true : false}
                  />
                ))}
              </video>
              <div
                className="vidio_popup_close_button inspired-video-popup-close"
                ref={closeBtnRef}
              >
                <button
                  onClick={() =>
                    onPlayInspireVideoClose(
                      videoRef?.current,
                      inspireRef?.current,
                      closeBtnRef?.current,
                      addVideoDataLayer
                    )
                  }
                >
                  <span aria-hidden="true"></span>
                </button>
              </div>
            </div>
          ) : null}
          <div
            className="cmp-img-caption"
            data-gbh-data-layer={`{"clickInternalLinks":"true","internalLinkPosition":"${eventPageType?.toLowerCase()}:${
              componentPosition || "teaser"
            }","internalLinkZoneName":"${eventPageType?.toLowerCase()}:${
              componentZone || "teaser"
            }","internalLinkURL":"n/a","internalLinkName":"n/a","eventName":"${eventPageType?.toLowerCase()}:${
              eventName || eventActionName
            }","internalLinkType":"${eventPageType?.toLowerCase()}:${
              componentLinkType || "teaser"
            }","eventType":"navigation","eventAction":"${eventPageType?.toLowerCase()}:${
              eventAction || eventActionName
            }"}`}
          >
            {sanitizeInnerHtml(caption)}
          </div>
        </div>

        <div className="cmp-teaser__content">
          {pretitle ? (
            <div className="cmp-teaser__pretitle">
              {sanitizeInnerHtml(pretitle)}
            </div>
          ) : null}
          {title ? (
            <div className="cmp-teaser__title">{sanitizeInnerHtml(title)}</div>
          ) : null}
          {description ? (
            <Description
              general={general}
              linkURL={linkCTA}
              linkURLRef={linkURLRef}
              linkNewTab={linkNewTab}
              description={description}
              mainTitle={mainTitle}
              componentPosition={componentPosition}
              componentLinkType={componentLinkType}
              pageType={pageType}
              linkDescription={linkDescription}
            />
          ) : null}
          {videoActionsEnabled === "true" || enablePopUPVideo === "true" ? (
            <div className="cmp-teaser__action-container">
              {actionsEnabled ? (
                <div className="cta_link">
                  {actions.map((action, index) => {
                    const {
                      playVideoCTA = false,
                      primaryCTA = false,
                      secondaryCTA = false,
                      url = "",
                      title = "",
                      newTab = "",
                      urlRef = "",
                    } = action
                    return (
                      <Action
                        key={`video-action-${index + 1}-${getRandom()}`}
                        primaryCTA={convertStringToBoolean(primaryCTA)}
                        secondaryCTA={convertStringToBoolean(secondaryCTA)}
                        playVideoCTA={convertStringToBoolean(playVideoCTA)}
                        link={url}
                        text={title}
                        newTab={convertStringToBoolean(newTab)}
                        urlRef={urlRef}
                        id={dynamicId}
                        pageType={pageType}
                        linkAll={convertStringToBoolean(linkAll)}
                        mainTitle={mainTitle}
                        dataLayerLinkType={dataLayerLinkType}
                        eventName={eventName}
                        eventAction={eventAction}
                        componentPosition={componentPosition}
                        propertiesPageType={propertiesPageType}
                        playVideoCTAcb={handlePlayVideoInModal}
                      />
                    )
                  })}
                </div>
              ) : null}

              {enablePopUPVideo === "true" ? (
                <div className="play_modal_video_cta">
                  <button
                    className={`playVideoCta ${
                      textLinkCta === "true" ? "textLinkCta" : ""
                    } ${
                      textLinkCtaOffWhite === "true"
                        ? "textLinkCtaoffwhite"
                        : ""
                    } ${blackBgCta === "true" ? "blackBgCta" : ""} ${
                      offWhiteBg === "true" ? "offWhiteBg" : ""
                    }`}
                    onClick={handlePlayInspireVideo}
                  >
                    <span>{modalCta}</span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          {/* Enable favorite and share button on video */}
          {enableFav || enableShare ? (
            <div className="cmp-teaser__icons">
              {enableFav ? (
                <div
                  className={`favorite fav-video-trigger${
                    isFavourited ? " favorite-filled" : ""
                  }`}
                  tabIndex="0"
                  onClick={event =>
                    onFavVideo(
                      event,
                      videoDesktop,
                      videoDesktop,
                      alt,
                      componentLinkType || caption,
                      videoTitle ||
                        componentLinkType.toLowerCase() ||
                        "hero video",
                      "",
                      favVideoId,
                      res => {
                        setIsFavourited(res)
                      }
                    )
                  }
                />
              ) : null}
              {enableShare ? (
                <div
                  className="share share-video-trigger"
                  tabIndex="0"
                  aria-label="share"
                  data-gbh-data-layer={`{"clickInternalLinks":"true","internalLinkPosition":"${pageType?.toLowerCase()}","internalLinkZoneName":"${pageType?.toLowerCase()}:${
                    componentZone?.toLowerCase() || "teaser"
                  }","internalLinkURL":"n/a","internalLinkName":"share","eventName":"${pageType?.toLowerCase()}:share", "internalLinkType":"${pageType?.toLowerCase()}:${
                    componentLinkType?.toLowerCase() || "share"
                  }","eventType":"navigation","eventAction":"${pageType?.toLowerCase()}:share"}`}
                  data-gbh-clickable="true"
                  onClick={e =>
                    onShareVideo(e, () => setShowSocialShare(!showSocialShare))
                  }
                >
                  {showSocialShare ? (
                    <div className="share-cmp" style={{ display: "block" }}>
                      <SocialShare
                        componentProp={{ assetUrl: videoDesktop }}
                        enableDownload={false}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

export default React.memo(Video)
